const SENSITIVE_KEY_PATTERN = /(?:^|_|-)(?:access|refresh|auth|token|password|secret|cookie|csrf|authorization|state|code|verifier|jwt|session)(?:$|_|-)/i;
const SECRET_VALUE_PATTERN = /^(?:bearer\s+)?[a-z0-9\-_=.]{24,}$/i;
const MAX_DEPTH = 4;
const MAX_ARRAY_ITEMS = 10;

const isProduction = process.env.NODE_ENV === 'production';
const debugEnabled = process.env.REACT_APP_ENABLE_CLIENT_DEBUG_LOGS === 'true' && !isProduction;
const infoEnabled = process.env.REACT_APP_ENABLE_CLIENT_INFO_LOGS === 'true' && !isProduction;
const consoleEnabledInProd = process.env.REACT_APP_ENABLE_CLIENT_CONSOLE_IN_PROD === 'true';

const maskSecret = (value) => {
  if (typeof value !== 'string') {
    return '[REDACTED]';
  }

  if (value.length <= 8) {
    return '[REDACTED]';
  }

  return `${value.slice(0, 3)}***${value.slice(-2)}`;
};

const shouldRedactKey = (key) => typeof key === 'string' && SENSITIVE_KEY_PATTERN.test(key);

const sanitizeValue = (value, key, depth, seen) => {
  if (value === null || value === undefined) {
    return value;
  }

  if (shouldRedactKey(key)) {
    return maskSecret(value);
  }

  if (typeof value === 'string') {
    return SECRET_VALUE_PATTERN.test(value.trim()) ? maskSecret(value.trim()) : value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: isProduction ? undefined : value.stack
    };
  }

  if (depth >= MAX_DEPTH) {
    return '[Truncated]';
  }

  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY_ITEMS).map((item) => sanitizeValue(item, key, depth + 1, seen));
  }

  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '[Circular]';
    }

    seen.add(value);
    const entries = Object.entries(value).map(([entryKey, entryValue]) => [
      entryKey,
      sanitizeValue(entryValue, entryKey, depth + 1, seen)
    ]);
    return Object.fromEntries(entries);
  }

  return String(value);
};

const sanitizeContext = (context) => sanitizeValue(context, '', 0, new WeakSet());

const getTracking = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.Sentry ?? null;
};

const emitToConsole = (level, scope, message, context, error) => {
  if (isProduction && !consoleEnabledInProd) {
    return;
  }

  if (level === 'debug' && !debugEnabled) {
    return;
  }

  if (level === 'info' && !infoEnabled && !debugEnabled) {
    return;
  }

  const method = typeof console?.[level] === 'function' ? level : 'log';
  const prefix = `[${scope}] ${message}`;

  if (error) {
    console[method](prefix, context, error);
    return;
  }

  if (context !== undefined) {
    console[method](prefix, context);
    return;
  }

  console[method](prefix);
};

const forwardToTracking = (level, scope, message, context, error) => {
  const tracking = getTracking();
  if (!tracking) {
    return;
  }

  const payload = {
    tags: {
      client_scope: scope,
      client_level: level,
      client_kind: context?.kind || 'system'
    },
    extra: context
  };

  if (error) {
    tracking.captureException(error, payload);
    return;
  }

  if (level === 'error' || context?.forward === true) {
    tracking.captureMessage(`${scope}: ${message}`, level === 'error' ? 'error' : 'warning');
  }
};

const normalizeErrorArgs = (maybeError, maybeContext) => {
  if (maybeError instanceof Error) {
    return {
      error: maybeError,
      context: maybeContext
    };
  }

  if (maybeError && typeof maybeError === 'object' && !Array.isArray(maybeError)) {
    return {
      error: null,
      context: maybeContext ? { ...maybeError, ...maybeContext } : maybeError
    };
  }

  if (typeof maybeError === 'string') {
    return {
      error: new Error(maybeError),
      context: maybeContext
    };
  }

  return {
    error: null,
    context: maybeContext
  };
};

export const createClientLogger = (scope) => {
  const emit = (level, message, rawContext, rawError) => {
    const context = rawContext === undefined ? undefined : sanitizeContext(rawContext);
    const error = rawError ? sanitizeContext(rawError) : null;

    emitToConsole(level, scope, message, context, error);
    forwardToTracking(level, scope, message, context, rawError instanceof Error ? rawError : null);
  };

  return {
    debug(message, context) {
      emit('debug', message, context);
    },
    info(message, context) {
      emit('info', message, context);
    },
    warn(message, context) {
      emit('warn', message, context);
    },
    error(message, maybeError, maybeContext) {
      const { error, context } = normalizeErrorArgs(maybeError, maybeContext);
      emit('error', message, context, error);
    },
    network(message, error, context) {
      emit('error', message, { kind: 'network', ...context }, error);
    },
    provider(message, error, context) {
      emit('error', message, { kind: 'provider', ...context }, error);
    },
    user(message, context) {
      emit('warn', message, { kind: 'user', ...context });
    }
  };
};

const logger = createClientLogger('LoopinoFrontend');

export default logger;
